import React, {useEffect, useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import classNames from "classnames"
import InputContainer from "./input-container"
import InputField from "./input-field"
import memo from "set-state-compare/build/memo"
import PropTypes from "prop-types"
import {useForm} from "@kaspernj/api-maker/build/form"

/**
 * @typedef {object} ComponentsUtilsInputProps
 * @property {unknown} autoRows
 * @property {unknown} containerProps
 * @property {object} dataSet
 * @property {string} defaultValue
 * @property {string} label
 * @property {number} minimumRows
 * @property {unknown} multiline
 * @property {string} name
 * @property {Function} onChangeText
 * @property {unknown} required
 * @property {object} style
 * @property {object} styles
 * @property {unknown} value
 */

/** @typedef {object} ComponentsUtilsInputState */

/**
 * @extends {ShapeComponent<ComponentsUtilsInputProps, ComponentsUtilsInputState>}
 */
class ComponentsUtilsInput extends ShapeComponent {
  static defaultProps = {
    autoRows: false,
    required: false
  }

  static propTypes = {
    autoRows: PropTypes.bool.isRequired,
    dataSet: PropTypes.object,
    defaultValue: PropTypes.string,
    label: PropTypes.string,
    minimumRows: PropTypes.number,
    name: PropTypes.string,
    onChangeText: PropTypes.func,
    required: PropTypes.bool.isRequired,
    style: PropTypes.object,
    styles: PropTypes.object
  }

  setup() {
    const {defaultValue, name, value} = this.props

    this.form = useForm()

    // Set initial value in form
    useMemo(() => {
      if (this.form && name) {
        if (value !== undefined) {
          this.form.setValue(name, value)
        } else if (defaultValue !== undefined) {
          this.form.setValue(name, defaultValue)
        }
      }
    }, [])

    useEffect(() => () => {
      if (this.form && name) {
        this.form.unsetValue(name)
      }
    }, [this.form, name])
  }

  containerProps() {
    const {containerProps, dataSet, ...containerRestProps} = this.props

    return Object.assign({}, containerRestProps, containerProps)
  }

  render() {
    const {autoRows, dataSet, defaultValue, label, minimumRows, name, onChangeText, required, style, styles, value, ...restProps} = this.props
    const {component, ...restDataSet} = dataSet || {}
    const actualDataSet = useMemo(() => {
      return Object.assign(
        {
          component: classNames("utils/input", component),
          name
        },
        restDataSet
      )
    }, [component, name, restDataSet])

    const actualStyle = this.stylingFor("textInput", style)
    const textInputExtraProps = {}
    const actualDefaultValue = value === undefined ? defaultValue : undefined

    if (autoRows && this.props.multiline && !("rows" in this.props)) {
      // If no rows prop is passed, calculate the number of rows based on the value
      let rows = value ? value.split("\n").length : 1

      if (minimumRows && minimumRows > rows) {
        rows = minimumRows
      }

      if (!("whiteSpace" in actualStyle)) {
        actualStyle.whiteSpace = "nowrap"
      }

      textInputExtraProps.rows = rows
    }

    return (
      <InputContainer {...this.containerProps()}>
        <InputField
          dataSet={actualDataSet}
          defaultValue={actualDefaultValue}
          onChangeText={this.tt.onChangeText}
          style={actualStyle}
          value={value}
          {...textInputExtraProps}
          {...restProps}
        />
      </InputContainer>
    )
  }

  onChangeText = (value) => {
    const {form} = this.tt
    const {name, onChangeText} = this.props

    if (form && name) {
      form.setValue(name, value)
    }

    if (onChangeText) {
      onChangeText(value)
    }
  }
}

export default memo(shapeComponent(ComponentsUtilsInput))
